/**
 * Turning 2FA on: the half of enrollment that had no UI at all.
 *
 * The backend deliberately splits this in two (`app/routes/admin.py`):
 * `/auth/2fa/enroll` mints a secret and stores it with `totp_enabled` still
 * false, and only `/auth/2fa/enroll/confirm` — which requires a working code —
 * flips the flag. That ordering is what stops a user from locking themselves
 * out of an account whose authenticator was never actually set up, so this
 * component never claims 2FA is on until the confirm call returns.
 *
 * The one hard rule here: once the confirm response arrives with the backup
 * codes, THIS component owns the screen until the user navigates away. The
 * parent's `enabled` prop will flip to true moments later (the page reloads
 * `/auth/me`), and reading that prop as "show the enabled state" would wipe the
 * only copy of the codes the user will ever see. `confirmed` therefore beats
 * `enabled` in the render below.
 */
import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'

import { ApiError, api } from '../api/client'
import type {
  EnrollConfirmResponse,
  EnrollStartResponse,
  EnrollmentTokenBody,
} from '../api/types'
import { Field, FormError, INPUT, SubmitButton } from '../auth/LoginPage'
import { useI18n } from '../i18n'
import type { TParams, TranslationKey } from '../i18n'
import { BackupCodes } from './BackupCodes'
import { CopyButton } from './SecretReveal'

/** Enrollment is always a live TOTP code; backup codes do not exist yet. */
const CODE_LENGTH = 6

/**
 * Errors are held as a key, not as a sentence.
 *
 * `describeEnrollError` runs when the request fails, which may be minutes
 * before the user switches language — and switching language must not leave
 * the one instruction on this screen stranded in the language they switched
 * away from. Translating at render time is what makes it follow.
 */
interface ErrorMessage {
  key: TranslationKey
  params?: TParams
}

export interface TwoFactorSetupProps {
  /** `MeResponse.totp_enabled` — the server's answer, not a local guess. */
  enabled: boolean
  /** Confirm succeeded: the page should re-read `/auth/me`. */
  onEnabled?: (result: EnrollConfirmResponse) => void
  /**
   * The scoped `enrollment` token, for a caller who holds no session yet: an
   * invitee redeeming their invite, or a login that answered
   * `enrollment_required`. When present it is sent with both calls in place of
   * the session cookie, and enrollment starts on its own — there is nothing
   * else this account can do, so making the user press a button first would be
   * ceremony. Absent on the profile page, which is unchanged.
   */
  enrollmentToken?: string
  /** This account cannot skip 2FA (`users.totp_required`); say so. */
  required?: boolean
  /**
   * Rendered under the backup codes as the step that ends enrollment. The codes
   * exist in readable form exactly once, so nothing may navigate away from them
   * on its own — the user says when they have them.
   */
  onAcknowledge?: () => void
  /**
   * The scoped token is spent: a 401 came back on the token path.
   *
   * Only meaningful alongside `enrollmentToken`. The ten-minute TTL starts when
   * the token is minted, not when the QR appears, so a first-time user who has
   * to find and install an authenticator app routinely blows it — and the
   * message ("sign in again") is unactionable while the screen renders no
   * control at all and the parent still holds the dead token in state. Wired
   * the way `TwoFactorPage`'s `onExpired` already is. Not a way past the gate:
   * the login it returns to answers `enrollment_required` and mints a fresh
   * token.
   */
  onExpired?: () => void
}

export function TwoFactorSetup({
  enabled,
  onEnabled,
  enrollmentToken,
  required = false,
  onAcknowledge,
  onExpired,
}: TwoFactorSetupProps) {
  const { t } = useI18n()
  const [enrollment, setEnrollment] = useState<EnrollStartResponse | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<ErrorMessage | null>(null)
  const [pending, setPending] = useState(false)
  /** The token path came back 401. The only move left is a fresh sign-in. */
  const [expired, setExpired] = useState(false)
  /** The user said they have the codes. Only then may this screen stop being
   *  the backup codes — see the rule at the top of the file. */
  const [acknowledged, setAcknowledged] = useState(false)
  /** The token path auto-starts; this makes it start exactly once, including
   *  under StrictMode's deliberate double-invoke, because a second
   *  `/auth/2fa/enroll` would replace the pending secret behind the QR the user
   *  is already scanning. */
  const autoStarted = useRef(false)

  /** Present only on the token path — the cookie path sends no body at all,
   *  because the session is the credential. */
  const tokenBody: EnrollmentTokenBody | undefined = enrollmentToken
    ? { enrollment_token: enrollmentToken }
    : undefined

  async function start() {
    if (pending) return
    setPending(true)
    setError(null)
    try {
      setEnrollment(await api.post<EnrollStartResponse>('/auth/2fa/enroll', tokenBody))
    } catch (err) {
      setError(describeEnrollError(err, enrollmentToken !== undefined))
      if (isExpiredToken(err, enrollmentToken)) setExpired(true)
    } finally {
      setPending(false)
    }
  }

  useEffect(() => {
    if (!enrollmentToken || autoStarted.current) return
    autoStarted.current = true
    void start()
    // Deliberately keyed on the token alone: `start` is re-created every render
    // and re-running this effect for it would re-POST the enrollment, replacing
    // the pending secret behind a QR the user may already have scanned.
  }, [enrollmentToken])

  async function confirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return
    if (code.length !== CODE_LENGTH) {
      setError({
        key: 'account.twoFactorSetup.error.codeLength',
        params: { length: CODE_LENGTH },
      })
      return
    }
    setPending(true)
    setError(null)
    try {
      const result = await api.post<EnrollConfirmResponse>('/auth/2fa/enroll/confirm', {
        code,
        ...tokenBody,
      })
      setBackupCodes(result.backup_codes)
      setCode('')
      onEnabled?.(result)
    } catch (err) {
      // The pending secret survives a wrong code, so the QR and the input stay
      // exactly where they were: re-scanning would only produce the same codes.
      setError(describeConfirmError(err, enrollmentToken !== undefined))
      if (isExpiredToken(err, enrollmentToken)) setExpired(true)
      setCode('')
    } finally {
      setPending(false)
    }
  }

  return (
    <section className="space-y-4 rounded-lg border border-border bg-surface p-6 shadow-sm">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-fg">{t('domain.twoFactor')}</h2>
        <p data-testid="2fa-requirement" className="text-xs text-muted">
          {required
            ? t('account.twoFactorSetup.requirementRequired')
            : t('account.twoFactorSetup.requirement')}
        </p>
      </header>

      {expired ? (
        // Everything else on this screen would 401: the credential it runs on
        // is gone. One control, and it goes where the message says.
        <div className="space-y-3">
          <FormError message={error && t(error.key, error.params)} />
          <button
            type="button"
            onClick={onExpired}
            className="w-full rounded-md bg-accent px-3 py-2 text-sm font-medium text-white"
          >
            {t('account.twoFactorSetup.signInAgain')}
          </button>
        </div>
      ) : backupCodes !== null && !acknowledged ? (
        // Beats `enabled` on purpose: reload() is about to flip that prop and
        // must not take the codes with it.
        <>
          <p data-testid="2fa-status" className="text-sm font-medium text-healthy">
            {t('account.twoFactorSetup.enabledNow')}
          </p>
          <BackupCodes codes={backupCodes} />
          <button
            type="button"
            onClick={() => {
              // Order matters only on the profile path, where nothing navigates
              // away: acknowledging is what replaces the codes with the enabled
              // state, so the dismissal is explicit on both paths.
              setAcknowledged(true)
              onAcknowledge?.()
            }}
            className="w-full rounded-md bg-accent px-3 py-2 text-sm font-medium text-white"
          >
            {t('account.twoFactorSetup.acknowledge')}
          </button>
        </>
      ) : acknowledged || enabled ? (
        // `acknowledged` beats a stale `enabled={false}`: the confirm has
        // landed, so the enabled state is the truth even in the moment before
        // the parent's `/auth/me` refetch returns. Without it the profile page
        // would drop the user back onto the QR form they just finished with.
        <EnabledState />
      ) : enrollment === null ? (
        <div className="space-y-3">
          <FormError message={error && t(error.key, error.params)} />
          <button
            type="button"
            onClick={() => void start()}
            disabled={pending}
            aria-busy={pending}
            className="rounded-md bg-accent px-3 py-2 text-sm font-medium text-white
                       disabled:opacity-60"
          >
            {t('account.twoFactorSetup.start')}
          </button>
        </div>
      ) : (
        <form onSubmit={confirm} noValidate className="space-y-4">
          <ol className="space-y-4 text-sm text-fg">
            <li className="space-y-2">
              <p className="font-medium">{t('account.twoFactorSetup.step1')}</p>
              {/* A data: URI rendered by segno server-side — no QR library in
                  the bundle, and no image request to a third party carrying the
                  secret in its URL. */}
              <img
                src={enrollment.qr_data_uri}
                alt={t('account.twoFactorSetup.qrAlt')}
                width={180}
                height={180}
                className="rounded-md border border-border bg-white p-2"
              />
            </li>
            <li className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium">{t('account.twoFactorSetup.step2')}</p>
                {/* The same treatment the invite token and the temporary
                    password get. This is the documented fallback for anyone
                    enrolling on the same machine as their authenticator, and a
                    mistyped base32 secret surfaces at the confirm step as
                    "that code was not accepted" — which reads as a clock
                    problem and sends people hunting in the wrong place. */}
                <CopyButton
                  label={t('account.twoFactorSetup.setupKey')}
                  value={enrollment.secret}
                />
              </div>
              <code className="num block select-all break-all rounded-md border border-border
                               bg-bg px-3 py-2 text-sm text-fg">
                {enrollment.secret}
              </code>
            </li>
            <li className="space-y-2">
              <p className="font-medium">{t('account.twoFactorSetup.step3')}</p>
              <Field id="enroll-code" label={t('account.twoFactorSetup.codeLabel')}>
                <input
                  id="enroll-code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={CODE_LENGTH}
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))
                  }
                  className={`${INPUT} font-mono tracking-[0.3em]`}
                />
              </Field>
            </li>
          </ol>

          <FormError message={error && t(error.key, error.params)} />

          <SubmitButton pending={pending}>{t('account.twoFactorSetup.submit')}</SubmitButton>
        </form>
      )}
    </section>
  )
}

/**
 * Already on. No re-enrollment button: `/auth/2fa/enroll` answers 409 for an
 * enabled account, so a button here could only ever produce an error, and
 * rotating the secret is not a thing this product supports. Losing the
 * authenticator is a superadmin recovery (`/api/v1/admin/users/{id}/disable-2fa`).
 */
function EnabledState() {
  const { t } = useI18n()

  return (
    <div className="space-y-2">
      <p data-testid="2fa-status" className="text-sm font-medium text-healthy">
        {t('account.twoFactorSetup.enabledTitle')}
      </p>
      <p className="text-xs text-muted">{t('account.twoFactorSetup.enabledHelp')}</p>
    </div>
  )
}

/** A 401 on the token path, i.e. the scoped token is spent. A 401 on the
 *  cookie path means an expired session, which the interceptor handles. */
function isExpiredToken(err: unknown, enrollmentToken?: string): boolean {
  return enrollmentToken !== undefined && err instanceof ApiError && err.status === 401
}

function describeEnrollError(err: unknown, viaToken = false): ErrorMessage {
  if (err instanceof ApiError) {
    if (err.status === 401 && viaToken) {
      // The scoped token lives ten minutes. Nothing here can renew it, and the
      // login it sends them back to hands out a fresh one for the same gate.
      return { key: 'account.twoFactorSetup.error.expiredLink' }
    }
    if (err.status === 409) {
      return { key: 'account.twoFactorSetup.error.alreadyEnabledReload' }
    }
    if (err.status === 403) {
      // `get_active_user` shuts every route but the three escape hatches while
      // must_change_password is set, so an account on a temporary password
      // cannot pin an authenticator to itself before its owner rotates it.
      return { key: 'account.twoFactorSetup.error.mustChangePassword' }
    }
    if (err.status === 401) return { key: 'account.twoFactorSetup.error.sessionExpired' }
    return {
      key: 'account.twoFactorSetup.error.enrollFailed',
      params: { status: err.status },
    }
  }
  return { key: 'account.twoFactorSetup.error.network' }
}

function describeConfirmError(err: unknown, viaToken = false): ErrorMessage {
  if (err instanceof ApiError) {
    if (err.status === 401 && viaToken) {
      return { key: 'account.twoFactorSetup.error.expiredLink' }
    }
    if (err.status === 400) {
      // Also covers "start enrollment first", which only happens if the pending
      // secret was replaced from another tab; retrying the code is still the
      // first thing to try, and the clock is the usual culprit.
      //
      // On the token path there is a likelier first suspect. Every entry into
      // enrollment re-POSTs /auth/2fa/enroll and REPLACES the pending secret,
      // so anyone who came back through a second login (or a browser Back) has
      // an older, identical-looking "TikHub" entry in their app that can never
      // produce a valid code. Sending them to check their clock sends them to
      // the wrong place. The profile path cannot reach that state, so it keeps
      // the shorter sentence.
      return viaToken
        ? { key: 'account.twoFactorSetup.error.codeRejectedToken' }
        : { key: 'account.twoFactorSetup.error.codeRejected' }
    }
    if (err.status === 409) return { key: 'account.twoFactorSetup.error.alreadyEnabled' }
    if (err.status === 422) {
      return {
        key: 'account.twoFactorSetup.error.codeLength',
        params: { length: CODE_LENGTH },
      }
    }
    if (err.status === 401) return { key: 'account.twoFactorSetup.error.sessionExpired' }
    return {
      key: 'account.twoFactorSetup.error.confirmFailed',
      params: { status: err.status },
    }
  }
  return { key: 'account.twoFactorSetup.error.network' }
}
